import Link from 'next/link'
import React from 'react'
import moment from 'moment'

import {
  Row,
  Col,
  CardBody,
  Card,
  CardTitle,
  CardText,
  Badge,
  Spinner,
  CardSubtitle,
  Media,
  CardFooter
} from 'reactstrap'

import useTranslation from '../hooks/UseTranslation'
import UserActivityBadge from './UserActivityBadge'
import ListingIcons from './ListingIcons'
import DayIcon from './DayIcon'
import Stars from './Stars'
import { firstLetter } from 'common'
import { getLocationString, getCurrencySign } from 'common'
import Image from './CustomImage'
import InfiniteScroll from 'react-infinite-scroll-component'
import { PacksApi } from 'typescript-axios'
import { getAxiosParams } from '../utils/client'
import { UserContext } from '../contexts/UserContext'
import { getFormattedDateAndRange, offsetDate } from 'common'
import ParticipantsBadge from './ParticipantsBadge'
import { getFirstClassDiscountBadge } from '../pages/marketing'

const PACKS_SERVICE = new PacksApi(getAxiosParams())

export const ListingCard = ({ listing, remainingTickets }) => {
  const { t } = useTranslation()

  const { price, owner } = listing
  const locs = getLocationString(listing, t)

  const { dateStartFormatted, timeRangeFormatted } = getFormattedDateAndRange(
    listing,
    true
  )
  const info = (
    <span>
      {dateStartFormatted} {timeRangeFormatted}
    </span>
  )
  const hasRating = owner.n_reviews > 5 || owner.rating > 3
  const ownerTickets = remainingTickets ? remainingTickets[owner.id] : null
  const hasTickets =
    ownerTickets && ownerTickets[listing.is_online ? 'online' : 'physical'] > 0

  return (
    <Col key={listing.slug} sm='6' xl='4' className='mb-5 hover-animate'>
      <Card className='h-100 border-0 shadow'>
        <Link href={'/classes/[slug]'} as={`/classes/${listing.slug}`}>
          <a className='tile-link' target='_blank' rel='noreferrer' />
        </Link>
        <div
          className='card-img-top gradient-overlay'
          style={{ minHeight: '200px', maxHeight: '200px' }}
        >
          <Image
            src={
              listing.cover ??
              `/content/img/activities/default-${listing.activity.id}.jpg`
            }
            alt={listing.title}
            layout='fill'
            loading={'eager'}
            objectFit='cover'
            className='img-fluid'
            style={{
              minWidth: '100%',
              maxWidth: '100%',
              minHeight: '100%',
              maxHeight: '100%'
            }}
          />
          <div className='card-img-overlay-bottom z-index-20'>
            <Media className='text-white text-sm align-items-center'>
              <div className='avatar avatar-border-white mr-2 overflow-hidden'>
                <Image
                  loading={'eager'}
                  objectFit='cover'
                  layout='fill'
                  src={owner.avatar ?? '/content/img/avatar/dummy-user.jpg'}
                  alt={owner.name_display}
                />
              </div>
              <Media body>
                <strong>{owner.name_display}</strong>
              </Media>
              {(owner.n_reviews > 5 || owner.rating > 3) && (
                <p className='flex-shrink-1 mb-0 card-stars text-right'>
                  <Stars stars={owner.rating} />
                </p>
              )}
            </Media>
          </div>
          <div className='card-img-overlay-top text-right'>
            <ParticipantsBadge listing={listing} />
            {/* <Badge
                            color="primary-light"
                            style={{ marginRight: '5px' }}><i className="fas fa-users fa-fw text-primary mr-2" />
                            {`${listing.num_participants}/${listing.max_participants}`}
                        </Badge> */}
            <Badge color='secondary-light' style={{ marginRight: '5px' }}>
              {firstLetter(listing.activity.name)}
            </Badge>
          </div>
        </div>
        <CardBody className='d-flex align-items-start'>
          <div className='w-100'>
            <CardTitle tag='h6' className='text-first-letter  mb-4'>
              <h5 className='ellipsis'>{listing.title}</h5>
              {/* <p className="subtitle font-weight-normal text-sm mb-2">{listing.is_online ? t("online") : listing.ppty.property_type.name}</p> */}
              <p className='subtitle font-weight-normal text-sm mb-2'>
                <i
                  className={`fa ${
                    listing.is_online ? 'fa-globe' : 'fa-map-marker-alt'
                  } text-muted mr-2`}
                />
                {listing.is_online
                  ? firstLetter(t('online'))
                  : listing.ppty.location.city}
              </p>
              <p className='mb-2'>{info}</p>
            </CardTitle>
          </div>
        </CardBody>
        <CardFooter
          style={{ backgroundColor: 'white', border: '0' }}
          className='mt-2'
        >
          {/* hotfix: got to add style={{position: "absolute", bottom: "1.25rem"}} to attach price to bottom */}
          <div
            className='d-flex text-muted align-items-center justify-content-between'
            style={{
              position: 'absolute',
              bottom: '1.25rem',
              left: 20,
              right: 20
            }}
          >
            {hasTickets ? (
              <Badge color='info-light'>
                {/* fixme: currency harcoded */}
                {t('packs_remaining_credits')}
              </Badge>
            ) : (
              getFirstClassDiscountBadge(
                listing.is_offers_apply_first_class_discount
                  ? owner.first_class_discount
                  : 0,
                owner.first_class_discount_type,
                'EUR',
                t
              )
            )}
            <div className='text-right flex-grow-1'>
              <span className='h4 text-primary'>
                {/* fixme: currency harcoded */}
                {`${listing.price} ${getCurrencySign('EUR')}`}
              </span>
            </div>
          </div>
        </CardFooter>
      </Card>
    </Col>
  )
}

const Listings = (props) => {
  const { t } = useTranslation()
  const [listings, setListings] = React.useState(props.listings)
  const [hasMore, setHasMore] = React.useState(props.hasMore)
  const { loggedUser, remainingTickets, setRemainingTickets } =
    React.useContext(UserContext)

  React.useEffect(() => {
    if (loggedUser) {
      PACKS_SERVICE.readRemainingTicketsApiV1PacksRemainingGet()
        .then((res) => setRemainingTickets(res.data))
        .catch((e) => {
          console.error(e)
        })
    }
  }, [loggedUser])

  React.useEffect(() => {
    setListings(props.listings)
  }, [props.listings])

  React.useEffect(() => {
    setHasMore(props.hasMore)
  }, [props.hasMore])

  let currentDay = moment(offsetDate(listings[0].datetime_start)).endOf('day')
  let items = [<DayIcon day={currentDay} showSubtitle />]
  let subItems = []
  listings.map((listing, index) => {
    const day = moment(offsetDate(listing.datetime_start))
    if (day.isAfter(currentDay)) {
      items = [
        items,
        <div className='mt-4'>
          <Row> {subItems} </Row>
        </div>
      ] // add listings corresponding to currentDay
      currentDay = day.endOf('day') // update current day
      items.push(<DayIcon day={currentDay} showSubtitle />) // add current day icon
      subItems = []
    }
    subItems.push(
      <ListingCard listing={listing} remainingTickets={remainingTickets} />
    )
  })
  items = [
    items,
    <div className='mt-4'>
      <Row> {subItems} </Row>
    </div>
  ] // add last listings
  return (
    <div>
      <InfiniteScroll
        dataLength={listings.length} //This is important field to render the next data
        next={props.next ?? (() => null)}
        hasMore={hasMore}
        loader={
          <div className='text-center'>
            {props.spinner ?? <Spinner color='primary' />}
          </div>
        }
        // endMessage={
        //     <p style={{ textAlign: 'center' }}>
        //         <b>Yay! You have seen it all</b>
        //     </p>
        // }
        scrollThreshold='800px'
        style={{ overflowX: 'visible', overflowY: 'visible' }}
        className='d-flex flex-column'
      >
        {items}
      </InfiniteScroll>
    </div>
  )
}

export default Listings
